import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import api from '../services/api.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const categoryLabels: Record<'real_estate' | 'vehicle' | 'mutuos' | 'other', string> = {
  real_estate: 'Imóvel',
  vehicle: 'Veículo',
  mutuos: 'Mútuos',
  other: 'Outros',
};

interface Receivable {
  id: string;
  description: string;
  type: 'real_estate' | 'vehicle' | 'mutuos' | 'other';
  category?: 'real_estate' | 'vehicle' | 'mutuos' | 'other';
  amount: number;
  remainingAmount: number;
  interestRate: number;
  startDate: string;
  dueDate: string;
  status: 'active' | 'paid' | 'overdue' | 'renegotiated';
  customer: string;
}

const Receivables: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('active');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [receivableToDelete, setReceivableToDelete] = useState<string | null>(null);

  const {
    data: receivables,
    isLoading,
    error,
    refetch,
  } = useQuery<Receivable[]>(
    'receivables',
    async () => {
      const response = await api.get('/receivables');
      return response.data;
    },
    { staleTime: 1000 * 60 * 5 }
  );

  const filteredReceivables =
    receivables?.filter(item => {
      const matchesSearch =
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.customer || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = selectedType === 'all' || (item.category ?? item.type) === selectedType;
      const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
      return matchesSearch && matchesType && matchesStatus;
    }) || [];

  const handleDelete = async (id: string) => {
    setReceivableToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (receivableToDelete) {
      try {
        await api.delete(`/receivables/${receivableToDelete}`);
        refetch();
        setShowDeleteModal(false);
        setReceivableToDelete(null);
      } catch (error) {
        console.error('Erro ao excluir recebível:', error);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setReceivableToDelete(null);
  };

  const activeItems = receivables?.filter(d => d.status === 'active') || [];
  const renegotiatedItems = receivables?.filter(d => d.status === 'renegotiated') || [];
  const overdueItems = receivables?.filter(d => d.status === 'overdue') || [];

  const totalActiveAmount = [...activeItems, ...renegotiatedItems].reduce(
    (sum, d) => sum + d.remainingAmount,
    0
  );
  const totalOverdueAmount = overdueItems.reduce((sum, d) => sum + d.remainingAmount, 0);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getNextDueDate = () => {
    const activeDueDates = activeItems
      .map(d => new Date(d.dueDate))
      .sort((a, b) => a.getTime() - b.getTime());

    return activeDueDates.length > 0 ? activeDueDates[0].toLocaleDateString('pt-BR') : 'N/A';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        Erro ao carregar recebíveis. Por favor, tente novamente.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BanknotesIcon className="h-8 w-8 mr-3 text-primary-600" />
            Gestão de Recebíveis
          </h1>
          <p className="text-gray-600 mt-2">Controle seus recebimentos futuros</p>
        </div>
        <button
          onClick={() => navigate('/receivables/new')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Novo Recebível
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[120px] flex items-center">
          <div className="flex items-center w-full">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4 w-full">
              <p className="text-sm font-medium text-gray-600 mb-3">Resumo</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-lg font-bold text-gray-900">{receivables?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ativos</p>
                  <p className="text-lg font-bold text-gray-900">{activeItems.length}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Em Atraso</p>
                  <p className="text-lg font-bold text-gray-900">{overdueItems.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[120px] flex items-center">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600">Valor em Atraso</p>
              <p className="text-2xl font-bold text-red-600 break-words whitespace-normal leading-tight">
                {formatCurrency(totalOverdueAmount)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[120px] flex items-center">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BuildingLibraryIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600">Valor Total Ativo</p>
              <p className="text-2xl font-bold text-gray-900 break-words whitespace-normal leading-tight">
                {formatCurrency(totalActiveAmount)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por descrição ou cliente..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
            >
              <option value="all">Todos os Tipos</option>
              <option value="real_estate">Imóvel</option>
              <option value="vehicle">Veículo</option>
              <option value="mutuos">Mútuos</option>
              <option value="other">Outros</option>
            </select>

            <select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativo</option>
              <option value="paid">Pago</option>
              <option value="overdue">Em Atraso</option>
              <option value="renegotiated">Renegociado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid */}
      {filteredReceivables.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReceivables.map(item => (
            <div
              key={item.id}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.description}</h3>
                  <p className="text-sm text-gray-500">{item.customer}</p>
                  <p className="text-xs text-gray-500">{categoryLabels[item.category ?? item.type]}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                  {formatStatus(item.status)}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Valor Original</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(item.amount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Valor Restante</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(item.remainingAmount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Taxa de Juros</span>
                  <span className="font-semibold text-gray-900">{item.interestRate}% a.m.</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Vencimento</span>
                  <span className={`font-semibold ${item.status === 'overdue' ? 'text-red-600' : 'text-gray-900'}`}>
                    {new Date(item.dueDate).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/receivables/${item.id}`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Ver
                </button>
                <button
                  onClick={() => navigate(`/receivables/${item.id}/edit`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Editar
                </button>
                {hasPermission('receivables.delete') && (
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum recebível encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedType !== 'all' || selectedStatus !== 'all'
              ? 'Tente ajustar os filtros de busca.'
              : 'Comece criando um novo recebível.'}
          </p>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-600" />
              <h3 className="text-lg font-medium text-gray-900 mt-2">Confirmar Exclusão</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Tem certeza que deseja excluir este recebível? Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receivables;